import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import Header from "./Header"
import Sidebar from "./Sidebar"
import NavMain from "./NavMain"
import use2FAPending from "../hooks/use2FAPending"
import MessageBanner from "../components/GlobalBanner/MessageBanner"
import PopupBanner from "../components/GlobalBanner/PopupBanner"
import ParticipantProfileBox from "../components/ParticipantProfileBox"
import StaffProfileBox from "../components/StaffProfileBox"
import { routesDef, gpraRoutesDef } from "../components/Routes/routesDef"
import { PROFILE_INFO_MODE } from "../components/Routes/routeMetadataConstants"
import { getMostSpecificMatchedRoute } from "../components/Routes/routeMatching"
import use2FADeadlineMet from "../hooks/use2FADeadlineMet"
import { UserContext } from "../context/userContext"
import { useProfileRenderContext } from "../context/profileRenderContext"
import { SidebarContentProvider } from "../context/sidebarContentContext"
import { canAccessRoute } from "../helpers/routeAccess"
import {
  getLocalStorageBoolean,
  setLocalStorageItem,
} from "../helpers/localStorage"
import styles from "../styles/Nav-Layout.module.css"

const SIDEBAR_COLLAPSED_STORAGE_KEY = "layout.sidebar.collapsed"
const ALL_ROUTES = [...routesDef, ...gpraRoutesDef]

function getInitialSidebarCollapsedState() {
  // Read sidebar collapsed state from local storage.
  return getLocalStorageBoolean(SIDEBAR_COLLAPSED_STORAGE_KEY, false)
}

export default function Layout({ children }) {
  const SIDEBAR_OVERLAY_CLOSE_DELAY_MS = 180
  const [showBanner, setShowBanner] = useState(true)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    getInitialSidebarCollapsedState,
  )
  const [isSidebarHoverOverlayOpen, setIsSidebarHoverOverlayOpen] = useState(false)
  const overlayCloseTimerRef = useRef(null)
  const [isBelowCollapseBreakpoint, setIsBelowCollapseBreakpoint] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 1024 : false,
  )
  const isPending2FA = use2FAPending()
  const deadlinePassed = use2FADeadlineMet()
  const location = useLocation()
  const { pathname } = location
  const userCtx = useContext(UserContext)
  const { overrideProfileInfoMode } = useProfileRenderContext()

  // Static route metadata used for both profile mode and access checks.
  // Resolve the currently matched route once per location change.
  const matchedRoute = useMemo(() => {
    return getMostSpecificMatchedRoute(ALL_ROUTES, pathname)
  }, [pathname])

  const activeProfileInfoMode = matchedRoute?.profileInfoMode
  const routeSidebarContentGroup = matchedRoute?.sidebarContentGroup || null

  // Dynamic pages can temporarily override route-level profile mode.
  const resolvedProfileInfoMode =
    overrideProfileInfoMode || activeProfileInfoMode

  // Use shared route auth rules so layout visibility matches RequireAdmin behavior.
  const hasProfileRouteAccess =
    !matchedRoute || canAccessRoute(matchedRoute, userCtx.session)

  const shouldRenderParticipantProfile =
    resolvedProfileInfoMode === PROFILE_INFO_MODE.PARTICIPANT &&
    hasProfileRouteAccess

  const shouldRenderStaffProfile =
    resolvedProfileInfoMode === PROFILE_INFO_MODE.STAFF && hasProfileRouteAccess

  const messages = [
    "Your account is required to have Two Factor Authentication enabled.",
  ]

  const handleCloseBanner = () => {
    setShowBanner(false)
  }

  const toggleSidebar = () => {
    if (isBelowCollapseBreakpoint) {
      return
    }

    setIsSidebarCollapsed((prev) => !prev)
  }

  useEffect(() => {
    const updateViewportFlag = () => {
      setIsBelowCollapseBreakpoint(window.innerWidth <= 1024)
    }

    updateViewportFlag()
    window.addEventListener("resize", updateViewportFlag)
    return () => window.removeEventListener("resize", updateViewportFlag)
  }, [])

  useEffect(() => {
    // Save sidebar collapsed state to local storage after state changes.
    setLocalStorageItem(
      SIDEBAR_COLLAPSED_STORAGE_KEY,
      isSidebarCollapsed,
    )
  }, [isSidebarCollapsed])

  const isSidebarEffectivelyCollapsed =
    isSidebarCollapsed || isBelowCollapseBreakpoint

  // Always clear pending close timers before changing overlay state.
  const clearOverlayCloseTimer = () => {
    if (overlayCloseTimerRef.current) {
      window.clearTimeout(overlayCloseTimerRef.current)
      overlayCloseTimerRef.current = null
    }
  }

  // Open preview overlay whenever sidebar is collapsed.
  const openSidebarHoverOverlay = () => {
    if (!isSidebarEffectivelyCollapsed) {
      return
    }

    clearOverlayCloseTimer()
    setIsSidebarHoverOverlayOpen(true)
  }

  // Delay close slightly so pointer can move from icon to overlay without flicker.
  const closeSidebarHoverOverlayWithDelay = () => {
    clearOverlayCloseTimer()
    overlayCloseTimerRef.current = window.setTimeout(() => {
      setIsSidebarHoverOverlayOpen(false)
      overlayCloseTimerRef.current = null
    }, SIDEBAR_OVERLAY_CLOSE_DELAY_MS)
  }

  // If sidebar mode changes to expanded, force overlay preview off.
  useEffect(() => {
    if (!isSidebarEffectivelyCollapsed) {
      clearOverlayCloseTimer()
      setIsSidebarHoverOverlayOpen(false)
    }
  }, [isSidebarEffectivelyCollapsed])

  // Cleanup timer on unmount to avoid state updates after component disposal.
  useEffect(() => {
    return () => {
      clearOverlayCloseTimer()
    }
  }, [])

  return (
    <>
      {isPending2FA && showBanner && (
        <MessageBanner messages={messages} onClose={handleCloseBanner} />
      )}

      {isPending2FA && deadlinePassed && (
        <PopupBanner messages={messages} onClose={handleCloseBanner} />
      )}

      <div className={`theme-wrapper expand ${styles.layoutRoot}`}>
        <Header
          isSidebarCollapsed={isSidebarEffectivelyCollapsed}
          onToggleSidebar={toggleSidebar}
          canToggleSidebar={true}
          isSidebarToggleClickDisabled={isBelowCollapseBreakpoint}
          onSidebarToggleHoverStart={openSidebarHoverOverlay}
          onSidebarToggleHoverEnd={closeSidebarHoverOverlayWithDelay}
        />
        <main
          className={`body-main ${styles.layoutBody} ${
            isSidebarEffectivelyCollapsed ? styles.layoutBodyCollapsed : ""
          }`}
        >
          <SidebarContentProvider routeSidebarContentGroup={routeSidebarContentGroup}>
            <div
              className={`${styles.bodyShell} ${
                isSidebarEffectivelyCollapsed ? styles.bodyShellCollapsed : ""
              }`}
            >
              <section className={styles.sidebarPane} aria-label="Sidebar region">
                <Sidebar
                  isCollapsed={isSidebarEffectivelyCollapsed}
                  isOverlayOpen={isSidebarHoverOverlayOpen}
                  onOverlayMouseEnter={openSidebarHoverOverlay}
                  onOverlayMouseLeave={closeSidebarHoverOverlayWithDelay}
                />
              </section>

              <section className={styles.mainContent} aria-label="Primary content region">
                <div className={styles.navMain}>
                  <NavMain
                  />
                </div>
                <div className={styles.pageContent}>
                  {shouldRenderParticipantProfile && <ParticipantProfileBox />}
                  {shouldRenderStaffProfile && <StaffProfileBox />}
                  {children}
                </div>
              </section>
            </div>
          </SidebarContentProvider>
        </main>
      </div>
    </>
  )
}
